import { AddressState } from './address-collection.state';
import { hasBackendAddrNumber, isUserCreatedAddress } from './address-number';

/**
 * The fields that say *which place* an address describes, joined. Everything the backend may revise
 * without the address becoming a different one — its number above all, but also the contact and
 * naming extras only the long form carries — is left out, so that an address the user created by
 * hand and the same address as the backend later sends it share this key.
 *
 * The city is taken without the `<city>;<state>` suffix OData sends for a USA address, so that an
 * address already split by {@link splitStateOfUSA} and a freshly fetched one still describe the same
 * place. That is what lets a caller match an address against the collection before handing it to the
 * address slice, which is where the splitting happens.
 *
 * @param address
 */
export function addressContentKey(address: AddressState): string {
    return [
        address.addrText,
        address.name1,
        address.name2,
        address.name3,
        address.name4,
        address.street,
        address.postCode,
        address.city.split(';')[0],
        address.country,
    ].join('\u0000');
}

/**
 * The address in `known` that `candidate` stands for, or `undefined` when the collection holds no
 * such address yet.
 *
 * The backend's own address number answers it outright. A candidate carrying one that the
 * collection does not know may still be an address it holds: the user creates addresses by hand,
 * and the backend hands out a number for one of them the moment it learns about it — so a manual
 * address describing the same place is what the candidate stands for, and taking it over is what
 * makes the number arrive rather than a duplicate. Two addresses that *both* carry a backend number
 * are never folded together on their content alone: the backend gave them separate numbers and it
 * is the one that decides.
 *
 * @param candidate the address to place
 * @param known the collection to place it in
 */
export function findKnownAddress(candidate: AddressState, known: readonly AddressState[]): AddressState | undefined {
    if (!hasBackendAddrNumber(candidate.addrNumber)) {
        return known.find((address) => addressContentKey(address) === addressContentKey(candidate));
    }
    return (
        known.find((address) => address.addrNumber === candidate.addrNumber) ??
        known.find(
            (address) =>
                !hasBackendAddrNumber(address.addrNumber) &&
                addressContentKey(address) === addressContentKey(candidate),
        )
    );
}

/**
 * The candidate under the identity of the address it stands for: the slice identity (`id`) and the
 * `addrKey` the legacy address matching goes by are taken from whichever address the collection
 * already holds for it, so that everything already pointing at that address — timesheet entries,
 * notification links — keeps pointing at it once the candidate is folded in.
 *
 * A candidate the collection does not know keeps the identity it was minted with.
 *
 * @param candidate the address to place
 * @param known the collection to place it in
 */
export function resolveAddressIdentity(candidate: AddressState, known: readonly AddressState[]): AddressState {
    const match = findKnownAddress(candidate, known);
    return match ? { ...candidate, id: match.id, addrKey: match.addrKey } : candidate;
}

/**
 * Whether a known address's tombstone survives being folded together with `candidate`.
 *
 * A local re-fold must not resurrect a deleted address — nothing about re-encountering the same
 * content locally means the address book's decision to delete it was wrong. Only the backend
 * re-sending the address (carrying a backend `addrNumber`, the same test `findKnownAddress` uses
 * for "the backend knows this address") clears it: that is new information the deletion did not
 * have.
 *
 * @param known the address already in the collection
 * @param candidate the address being folded in
 */
function survivingDeletedFlag(known: AddressState, candidate: AddressState): boolean {
    return known.deleted && !hasBackendAddrNumber(candidate.addrNumber);
}

/**
 * The collection with one address folded into it, dropping none of the ones it already holds: an
 * address the collection does not know is appended, and one it knows is updated in place under the
 * identity it already has (see {@link findKnownAddress}), so everything pointing at it — timesheet
 * entries, notification links — keeps pointing at it.
 *
 * Three things the collection holds survive an update, because they are not the candidate's to say:
 * whether the user hid the address, the short hideable `AddressSet` form, and — unless the candidate
 * carries a backend `addrNumber` — the tombstone (see {@link survivingDeletedFlag}). A notification
 * carries the same place twice, once as that short form and once as its own long customer form; the
 * picker offers the short one, so the long one never replaces it. Together those make the upsert
 * idempotent and order-independent — the same address may be folded in as often as it turns up.
 *
 * @param existing the current collection (not mutated)
 * @param candidate the address to fold in
 * @returns the collection with the candidate folded in, or `existing` itself when it already stood
 */
export function upsertAddress(existing: readonly AddressState[], candidate: AddressState): AddressState[] {
    const known = findKnownAddress(candidate, existing);
    if (!known) {
        return [...existing, candidate];
    }
    if (known.isFromAddressSet && !candidate.isFromAddressSet) {
        // The long customer form never replaces the short `AddressSet` form — but a backend
        // `addrNumber` on the candidate is still the backend re-sending the address, and has to
        // clear the tombstone and the out-of-scope flag even though nothing else about the
        // candidate is taken over here: the address is, by definition, part of the payload that
        // just arrived.
        return hasBackendAddrNumber(candidate.addrNumber) && (known.deleted || known.outOfScope)
            ? existing.map((address) => (address === known ? { ...known, deleted: false, outOfScope: false } : address))
            : [...existing];
    }
    return existing.map((address) =>
        address === known
            ? {
                  ...candidate,
                  id: known.id,
                  addrKey: known.addrKey,
                  visible: known.visible,
                  deleted: survivingDeletedFlag(known, candidate),
                  // An address reaching this branch is one an `NdpHeaderSet` just carried, so it is
                  // in scope by definition — unlike the tombstone, nothing about it survives.
                  outOfScope: false,
              }
            : address,
    );
}

/**
 * The collection with every address's `outOfScope` flag brought in line with one load's payload.
 *
 * "In scope" means the address's id is in `inScopeIds` — the ids `NotificationEffects.mergeAddresses`
 * collected from every notification's `addressSet` and `customerAddress` across the load that just
 * finished, after resolving each one to the identity the collection already holds for it (see
 * `resolveConvertedAddresses`). A user-created address ({@link isUserCreatedAddress}) is exempt and
 * always comes back `outOfScope: false`: the backend never had it to offer or withhold in the first
 * place, so it is not this rule's to decide.
 *
 * The flag is reversible, not a deletion: an address the backend stops sending is only hidden from
 * the pickers until it turns up again in a later `NdpHeaderSet`, at which point `upsertAddress`
 * clears the flag. Nothing about the address itself, or its resolvability by id, changes here.
 *
 * A load that returns no notifications at all — `inScopeIds` empty — takes every backend address out
 * of scope by this same rule, with no special case guarding it: a load that carried nothing says as
 * much about what is current as one that carried something, and the picker should reflect that
 * rather than keep offering whatever the previous load last said was current. This is an accepted
 * consequence of the rule, decided against being guarded for now.
 *
 * Reports "nothing changed" as `undefined` rather than by handing the collection back, matching
 * {@link mergeNewAddressesIntoCollection}: both are read by an effect that has to tell a real write
 * from a no-op, and an explicit `undefined` says so without the caller comparing references.
 *
 * @param existing the current address collection (not mutated)
 * @param inScopeIds the ids of every address the load carried
 * @returns the collection with `outOfScope` brought up to date, or `undefined` when nothing about it
 *   needed to change
 */
export function markAddressesOutOfScope(
    existing: readonly AddressState[],
    inScopeIds: ReadonlySet<string>,
): AddressState[] | undefined {
    let isChanged = false;
    const addresses = existing.map((address) => {
        const outOfScope = !isUserCreatedAddress(address) && !inScopeIds.has(address.id);
        if (outOfScope === address.outOfScope) {
            return address;
        }
        isChanged = true;
        return { ...address, outOfScope };
    });
    return isChanged ? addresses : undefined;
}

/**
 * Splits the `<city>;<state>` suffix OData sends for a USA address off `city`, selecting
 * `stateOfUSA` from it when the extracted code is one of `validStateCodes`. A city without the
 * separator, or a code the value help does not recognize, leaves the address untouched — in
 * particular, `stateOfUSA` is preserved rather than cleared, so an address that was already split
 * on a previous load and carries no more suffix keeps the state it was assigned then. Mirrors the
 * reverse encoding `mapAddress` applies when building the timesheet submit model
 * (`buildTimesheetOutModel`).
 *
 * @param address the address to split
 * @param validStateCodes the USA state codes the value help currently recognizes
 */
export function splitStateOfUSA(address: AddressState, validStateCodes: readonly string[]): AddressState {
    const [city, stateCode] = address.city.split(';');
    if (stateCode === undefined) {
        return address;
    }
    return {
        ...address,
        city,
        stateOfUSA: validStateCodes.includes(stateCode) ? stateCode : address.stateOfUSA,
    };
}

/**
 * Folds candidate addresses into the collection: a candidate {@link findKnownAddress} does not
 * recognize is added to the front, and one it matches — by backend `addrNumber`, or otherwise by
 * {@link addressContentKey}, i.e. the same *place* — updates that entry in place. There is no
 * third, "ignore" outcome: an earlier version of this function matched only on `addrKey`, so a
 * candidate describing a place the collection already held under a different (or absent) `addrKey`
 * fell through unnoticed — the same bug class this function exists to avoid, just triggered from
 * the other side. Extracted from the facade's former `addAddressesAndSave`; used by the address
 * merge effect.
 *
 * This is the same "same place" test {@link upsertAddress} uses, but the update it performs
 * deliberately differs in three ways, because every candidate reaching *this* path is one a
 * technician typed by hand and asked to have in the book (`AddressPickerComponent.saveNewAddress`,
 * and the legacy `AddressSelectExpandableComponent`) — not, as at `upsertAddress`, one that merely
 * turned up (e.g. a notification's addresses, reloaded on every fetch):
 *
 * - the candidate's typed values win over the known address's own, including `visible` — so
 *   re-typing a hidden address brings it back, for the same reason as the next point;
 * - it revives a tombstone (`deleted: false`) unconditionally, rather than only when the candidate
 *   carries a backend `addrNumber` — a hand-typed request is itself the new information that makes
 *   reviving correct, so it does not need to wait for the backend to say so;
 * - a candidate the collection does not know is inserted at the *front* (`unshift`), not appended —
 *   this collection's order is display order in the pickers;
 * - the entry it writes always carries `outOfScope: false`, not only `deleted: false` — every
 *   candidate reaching this path is user-created (`isUserCreatedAddress`), which is exempt from the
 *   scoping flag altogether, not merely in scope for now.
 *
 * One thing does carry over from `upsertAddress` unconditionally: a known address's backend
 * `addrNumber` is never overwritten by a manual one. Without that guard, retyping an address the
 * backend already knows would demote its real number to a `manual-` one, and `outboundAddrNumber`
 * would then submit `''` to SAP for an address the backend does in fact know.
 *
 * A second thing also carries over from `upsertAddress` unconditionally, for the same reason as
 * its own `known.isFromAddressSet && !candidate.isFromAddressSet` guard: a known `AddressSet`
 * address is the backend's short hideable form, and a candidate typed here is a local one, so it
 * may not overwrite it — not even with the takeovers above, which exist precisely to let a typed
 * candidate win. But refusing the candidate outright, the way `upsertAddress` does, is wrong here:
 * it would make the technician's "Add" button appear to do nothing. So instead of updating the
 * `AddressSet` entry, such a candidate is treated as if `findKnownAddress` had found no match at
 * all, and goes through the "does not exist" branch below — added as its own, independent entry.
 * The consequence is that duplicating a backend address and pressing "Add" without changing any of
 * the fields `addressContentKey` looks at produces a second entry describing the same place; that
 * is the accepted cost of not silently demoting the original.
 *
 * @param existing the current address collection (not mutated)
 * @param candidates the addresses to fold in (not mutated)
 * @returns the merged collection, or undefined when nothing changed
 */
export function mergeNewAddressesIntoCollection(
    existing: AddressState[],
    candidates: AddressState[],
): AddressState[] | undefined {
    let isChanged = false;
    const addresses = [...existing];

    candidates.forEach((candidate) => {
        const found = findKnownAddress(candidate, addresses);
        // A known AddressSet address is the backend's; a locally typed candidate may not overwrite
        // it (see the "second thing" in the doc comment above, mirroring upsertAddress's own
        // guard). Null it out here so the match is treated as if it did not exist, keeping a single
        // "no target → unshift" path below rather than a second copy of the insert.
        const known = found?.isFromAddressSet && !candidate.isFromAddressSet ? undefined : found;

        if (!known) {
            // Address does not exist → ADD
            addresses.unshift(candidate);
            isChanged = true;
            return;
        }

        // Address exists → UPDATE, keeping its identity so references resolved before the update
        // still target it. A hand-typed address is a request to have this place in the book, so it
        // revives a tombstoned entry rather than being written into one the pickers keep hiding.
        const index = addresses.indexOf(known);
        addresses[index] = {
            ...candidate,
            id: known.id,
            addrNumber:
                hasBackendAddrNumber(known.addrNumber) && !hasBackendAddrNumber(candidate.addrNumber)
                    ? known.addrNumber
                    : candidate.addrNumber,
            deleted: false,
            outOfScope: false,
        };
        isChanged = true;
    });

    return isChanged ? addresses : undefined;
}
